import { useState, useCallback, useRef } from 'react'
import { headlessAnalyze } from 'src/api/headless'
import URL from 'src/api/url'

const MAX_RECONNECT_ATTEMPTS = 3
const RECONNECT_DELAYS_MS = [1000, 2000, 4000]

const useAnalyzeStream = () => {
  const [analyzing, setAnalyzing] = useState(false)
  const [progress, setProgress] = useState(0)
  const [step, setStep] = useState('')
  const [result, setResult] = useState(null)
  const [partialResults, setPartialResults] = useState([])
  const [error, setError] = useState(null)
  const [jobId, setJobId] = useState(null)
  const [reconnecting, setReconnecting] = useState(false)
  const [reconnectAttempt, setReconnectAttempt] = useState(0)
  const eventSourceRef = useRef(null)
  const retryCountRef = useRef(0)
  const retryTimeoutRef = useRef(null)

  const connectToStream = useCallback((id) => {
    const streamUrl = `${URL.APP_URL_LLM}collection-builder/analyze/${id}/stream`

    return new Promise((resolve, reject) => {
      const connect = () => {
        if (eventSourceRef.current) {
          eventSourceRef.current.close()
        }

        const evtSource = new EventSource(streamUrl)
        eventSourceRef.current = evtSource

        evtSource.onmessage = event => {
          // Reset retry count on any successful message
          retryCountRef.current = 0
          setReconnecting(false)
          setReconnectAttempt(0)

          try {
            const eventData = JSON.parse(event.data)
            const pct = eventData.progress || 0
            setProgress(pct)
            setStep(eventData.current_step || '')

            if (eventData.partial_results) {
              setPartialResults(eventData.partial_results)
            }

            if (eventData.status === 'completed') {
              evtSource.close()
              setResult(eventData.result)
              setAnalyzing(false)
              resolve(eventData.result)
            } else if (eventData.status === 'failed') {
              evtSource.close()
              const errMsg = eventData.error || 'Unknown error'
              setError(errMsg)
              setAnalyzing(false)
              reject(new Error(errMsg))
            } else if (eventData.status === 'cancelled') {
              evtSource.close()
              setError('Job was cancelled')
              setAnalyzing(false)
              reject(new Error('Job was cancelled'))
            }
          } catch (e) {
            // parse error, ignore
          }
        }

        evtSource.onerror = () => {
          evtSource.close()

          if (retryCountRef.current < MAX_RECONNECT_ATTEMPTS) {
            retryCountRef.current += 1
            setReconnecting(true)
            setReconnectAttempt(retryCountRef.current)

            const delay = RECONNECT_DELAYS_MS[retryCountRef.current - 1]
            retryTimeoutRef.current = setTimeout(() => {
              connect()
            }, delay)
          } else {
            setReconnecting(false)
            setReconnectAttempt(0)
            setError('SSE connection lost')
            setAnalyzing(false)
            reject(new Error('SSE connection lost'))
          }
        }
      }

      connect()
    })
  }, [])

  const startAnalyze = useCallback(async (courses, processor = 'anthropic') => {
    setAnalyzing(true)
    setProgress(0)
    setStep('Starting...')
    setResult(null)
    setPartialResults([])
    setError(null)
    setJobId(null)
    setReconnecting(false)
    setReconnectAttempt(0)
    retryCountRef.current = 0
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current)
    }

    try {
      const body = {
        courses,
        processor
      }

      const data = await headlessAnalyze(body)
      const id = data.job_id
      setJobId(id)
      return connectToStream(id)
    } catch (e) {
      setError(e.message || 'Failed to start analyze')
      setAnalyzing(false)
      throw e
    }
  }, [connectToStream])

  const reconnectToJob = useCallback((id) => {
    setAnalyzing(true)
    setProgress(0)
    setStep('Reconnecting...')
    setResult(null)
    setPartialResults([])
    setError(null)
    setJobId(id)
    setReconnecting(false)
    setReconnectAttempt(0)
    retryCountRef.current = 0
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current)
    }
    return connectToStream(id)
  }, [connectToStream])

  const setResultDirectly = useCallback((data) => {
    setAnalyzing(false)
    setProgress(100)
    setStep('Done!')
    setResult(data)
    setPartialResults(data.chapters || [])
    setError(null)
  }, [])

  const cancel = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
      eventSourceRef.current = null
    }
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current)
    }
    setAnalyzing(false)
    setReconnecting(false)
  }, [])

  return {
    analyzing,
    progress,
    step,
    result,
    partialResults,
    error,
    jobId,
    reconnecting,
    reconnectAttempt,
    startAnalyze,
    reconnectToJob,
    setResultDirectly,
    cancel,
  }
}

export default useAnalyzeStream
